// src/frontend_react/src/pages/AdminDashboard/UserManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Table from '../../components/ui/Table/Table';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Modal from '../../components/ui/Modal/Modal';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';
import Input from '../../components/ui/Input/Input'; // For MFA code input

// Shared Admin Styles
import adminPageStyles from './styles.module.css'; // Assuming a shared style module

// API service
import {
  getAdminUsers,
  updateAdminUser,
  adminInitiateMfaSetup,
  adminVerifyAndEnableMfa,
} from '../../services/api'; // Added MFA APIs

// Icons
import {
  FiUsers,
  FiPlus,
  FiEdit,
  FiUserX,
  FiUserCheck,
  FiKey,
  FiXCircle,
  FiCheckCircle,
  FiShield,
} from 'react-icons/fi';

function UserManagementPage() {
  const [confirmationInput, setConfirmationInput] = useState('');

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationMessage, setOperationMessage] = useState(''); // For general success (e.g., status change)

  const [isSubmitting, setIsSubmitting] = useState(false); // Generic submitting state for any action

  // Status Change Modal
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [userForStatusChange, setUserForStatusChange] = useState(null);

  // MFA Setup Modal
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaUser, setMfaUser] = useState(null);
  const [mfaSetupDetails, setMfaSetupDetails] = useState(null); // { secret, provisioning_uri }
  const [mfaVerificationCode, setMfaVerificationCode] = useState('');
  const [mfaModalError, setMfaModalError] = useState('');
  const [mfaModalSuccess, setMfaModalSuccess] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      setOperationMessage(location.state.message);
      navigate(location.pathname, { replace: true, state: {} }); // Clear message from location state
    }
  }, [location, navigate]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedUsersData = await getAdminUsers();
      // Ensure the response is the array of users, not an object like { success: true, users: [...] }
      // Based on your api.js, getAdminUsers directly returns the data part of the response.
      if (Array.isArray(fetchedUsersData)) {
        setUsers(fetchedUsersData.sort((a, b) => a.id - b.id)); // Sort by ID or username
      } else if (fetchedUsersData && Array.isArray(fetchedUsersData.users)) {
        // If API wraps it
        setUsers(fetchedUsersData.users.sort((a, b) => a.id - b.id));
      } else {
        throw new Error(
          fetchedUsersData.message ||
            'Failed to fetch users: Unexpected response format.',
        );
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching users.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // --- User Status Change Logic ---
  const openStatusConfirmModal = useCallback((user) => {
    setUserForStatusChange(user);
    setConfirmationInput('');
    setShowStatusConfirmModal(true);
  }, []);

  const closeStatusConfirmModal = useCallback(() => {
    setUserForStatusChange(null);
    setShowStatusConfirmModal(false);
  }, []);

  const executeChangeUserStatus = useCallback(async () => {
    if (!userForStatusChange) return;
    const { id, username, is_active: currentIsActive } = userForStatusChange;
    const action = currentIsActive ? 'Deactivate' : 'Reactivate';
    const newStatus = !currentIsActive;

    setIsSubmitting(true);
    setOperationMessage('');
    setError(null);
    try {
      // Assuming updateAdminUser can handle just 'is_active'
      await updateAdminUser(id, { is_active: newStatus });
      setOperationMessage(
        `User '${username}' successfully ${action.toLowerCase()}d.`,
      );
      fetchUsers(); // Refresh list
    } catch (err) {
      let errMsg = err.message || `Failed to ${action.toLowerCase()} user.`;
      // Check for specific error message from backend about self-deactivation
      if (String(err.message).includes('deactivate your own account')) {
        errMsg = 'Operation failed: You cannot deactivate your own account.';
      }
      setError(errMsg);
    } finally {
      setIsSubmitting(false);
      closeStatusConfirmModal();
    }
  }, [userForStatusChange, fetchUsers, closeStatusConfirmModal]);

  // --- MFA Setup Logic ---
  const openMfaSetupModal = async (user) => {
    setMfaUser(user);
    setMfaSetupDetails(null);
    setMfaVerificationCode('');
    setMfaModalError('');
    setMfaModalSuccess('');
    setIsMfaModalOpen(true);
    setIsSubmitting(true); // Use general submitting state for modal loading too
    try {
      const response = await adminInitiateMfaSetup(user.id);
      if (response.success) {
        setMfaSetupDetails(response); // response should contain { secret, provisioning_uri }
      } else {
        setMfaModalError(
          response.message || 'Failed to initiate MFA setup process.',
        );
      }
    } catch (err) {
      setMfaModalError(err.message || 'Error contacting server for MFA setup.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaVerificationAndEnable = async (e) => {
    e.preventDefault();
    if (!mfaUser || !mfaVerificationCode) {
      setMfaModalError('Verification code is required.');
      return;
    }
    setIsSubmitting(true);
    setMfaModalError('');
    setMfaModalSuccess('');
    try {
      const response = await adminVerifyAndEnableMfa(
        mfaUser.id,
        mfaVerificationCode,
      );
      if (response.success) {
        setMfaModalSuccess(response.message || 'MFA enabled successfully!');
        fetchUsers(); // Refresh user list to show updated MFA status
        setTimeout(() => {
          // Close modal after success
          setIsMfaModalOpen(false);
          setMfaUser(null);
        }, 2500);
      } else {
        setMfaModalError(response.message || 'MFA verification failed.');
      }
    } catch (err) {
      setMfaModalError(err.message || 'Error during MFA verification process.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Navigation ---
  const handleEditUser = useCallback(
    (userId) => {
      navigate(`/admin/dashboard/users/edit/${userId}`); // Assuming you have this route
    },
    [navigate],
  );

  const handleCreateUser = useCallback(() => {
    navigate('/admin/dashboard/users/create'); // Assuming you have this route
  }, [navigate]);

  const columns = React.useMemo(
    () => [
      { Header: 'ID', accessor: 'id', width: 60 },
      { Header: 'Username', accessor: 'username' },
      { Header: 'Email', accessor: 'email' },
      {
        Header: 'Name',
        id: 'full_name',
        Cell: ({ row }) => {
          const firstName = row.first_name || '';
          const lastName = row.last_name || '';
          const name = `${firstName} ${lastName}`.trim();
          return (
            name || (
              <Typography variant="caption" color="placeholder">
                N/A
              </Typography>
            )
          );
        },
      },
      {
        Header: 'Organization',
        accessor: 'organization',
        Cell: ({ value }) =>
          value || (
            <Typography variant="caption" color="placeholder">
              N/A
            </Typography>
          ),
      },
      {
        Header: 'Roles',
        accessor: 'roles',
        Cell: ({ value }) =>
          value && value.length > 0 ? (
            value.join(', ')
          ) : (
            <Typography variant="caption" color="placeholder">
              No Roles
            </Typography>
          ),
      },
      {
        Header: 'Status',
        accessor: 'is_active',
        width: 100,
        Cell: ({ value }) => (
          <Badge variant={value ? 'success' : 'default'} size="sm">
            {value ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        Header: 'MFA',
        accessor: 'is_totp_enabled',
        width: 80,
        Cell: ({ value }) => (
          <Icon
            as={value ? FiShield : FiXCircle}
            color={value ? 'success' : 'placeholder'}
            title={value ? 'MFA Enabled' : 'MFA Disabled'}
          />
        ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        width: 'auto',
        minWidth: 280,
        Cell: ({ row }) => (
          <HStack spacing="sm" className={adminPageStyles.actionsCell}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditUser(row.id)}
              leftIcon={<Icon as={FiEdit} size="sm" />}
              disabled={isSubmitting}
            >
              Edit
            </Button>
            <Button
              variant={row.is_active ? 'warning' : 'success'}
              size="sm"
              onClick={() => openStatusConfirmModal(row)}
              leftIcon={
                <Icon as={row.is_active ? FiUserX : FiUserCheck} size="sm" />
              }
              disabled={isSubmitting}
            >
              {row.is_active ? 'Deactivate' : 'Reactivate'}
            </Button>
            {!row.is_totp_enabled && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openMfaSetupModal(row)}
                leftIcon={<Icon as={FiKey} size="sm" />}
                disabled={isSubmitting}
              >
                Setup MFA
              </Button>
            )}
          </HStack>
        ),
      },
    ],
    [isSubmitting, handleEditUser, openStatusConfirmModal, openMfaSetupModal],
  );

  // Main loading state for initial fetch
  if (isLoading && users.length === 0) {
    return (
      <Card
        title="User Management"
        titleIcon={<Icon as={FiUsers} />}
        className={adminPageStyles.adminSectionCard}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="xl"
        >
          <Spinner size="lg" />
          <Typography mt="md" color="secondary">
            Loading users...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      title="User Management"
      titleIcon={<Icon as={FiUsers} />}
      className={adminPageStyles.adminSectionCard}
      headerActions={
        <Button
          variant="primary"
          onClick={handleCreateUser}
          leftIcon={<Icon as={FiPlus} />}
          disabled={isSubmitting || isLoading}
        >
          Create New User
        </Button>
      }
    >
      <Stack spacing="lg">
        <Typography variant="body1" color="secondary">
          View, create, edit, and manage user accounts and their roles within
          the platform.
        </Typography>

        {error && (
          <Alert
            variant="error"
            title="Page Error"
            onDismiss={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {operationMessage && (
          <Alert
            variant="success"
            title="Operation Successful"
            onDismiss={() => setOperationMessage('')}
          >
            {operationMessage}
          </Alert>
        )}

        {/* Table display logic */}
        {!isLoading && users.length === 0 && !error ? (
          <Alert variant="info">
            No users found. Click "Create New User" to add one.
          </Alert>
        ) : (
          <Table
            columns={columns}
            data={users}
            isLoading={isLoading && users.length > 0}
          /> // Show spinner over table if refreshing
        )}

        {/* Status Change Confirmation Modal */}
        {showStatusConfirmModal && userForStatusChange && (
          <Modal
            title={`${
              userForStatusChange.is_active ? 'Deactivate' : 'Reactivate'
            } User: ${userForStatusChange.username}`}
            isOpen={showStatusConfirmModal}
            onClose={closeStatusConfirmModal}
            size="md"
            footer={
              <HStack spacing="sm" justifyContent="flex-end">
                <Button
                  variant="outline"
                  onClick={closeStatusConfirmModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant={
                    userForStatusChange.is_active ? 'destructive' : 'success'
                  }
                  onClick={executeChangeUserStatus}
                  isLoading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    (userForStatusChange.is_active &&
                      confirmationInput !== userForStatusChange.username)
                  }
                >
                  {userForStatusChange.is_active
                    ? 'Confirm Deactivate'
                    : 'Confirm Reactivate'}
                </Button>
              </HStack>
            }
          >
            <Stack spacing="md">
              <Typography>
                Are you sure you want to{' '}
                {userForStatusChange.is_active ? 'deactivate' : 'reactivate'}{' '}
                user "<strong>{userForStatusChange.username}</strong>"?
              </Typography>
              {userForStatusChange.is_active && (
                <>
                  <Alert variant="warning">
                    Deactivating a user will revoke their access immediately.
                    They will not be able to log in.
                  </Alert>
                  <Input
                    label={
                      <span>
                        Please type{' '}
                        <strong>{userForStatusChange.username}</strong> to
                        confirm deactivation.
                      </span>
                    }
                    id="status-change-confirm-input"
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.target.value)}
                    autoFocus
                    disabled={isSubmitting}
                  />
                </>
              )}
            </Stack>
          </Modal>
        )}

        {/* MFA Setup Modal */}
        {isMfaModalOpen && mfaUser && (
          <Modal
            isOpen={isMfaModalOpen}
            onClose={() => {
              if (!isSubmitting) {
                setIsMfaModalOpen(false);
                setMfaUser(null);
              }
            }}
            title={`Setup MFA for ${mfaUser.username}`}
            size="md" // Medium size for QR code
          >
            <Stack spacing="md">
              {mfaModalError && (
                <Alert variant="error" onDismiss={() => setMfaModalError('')}>
                  {mfaModalError}
                </Alert>
              )}
              {mfaModalSuccess && (
                <Alert variant="success">{mfaModalSuccess}</Alert>
              )}

              {isSubmitting &&
                !mfaSetupDetails &&
                !mfaModalError &&
                !mfaModalSuccess && (
                  <Stack alignItems="center" p="lg">
                    <Spinner label="Initiating MFA setup..." />
                  </Stack>
                )}

              {mfaSetupDetails && !mfaModalSuccess && (
                <>
                  <Typography fontWeight="medium">
                    1. Scan QR with Authenticator App
                  </Typography>
                  <Box
                    textAlign="center"
                    p="md"
                    border="1px solid var(--cipher-color-border-default)"
                    borderRadius="md"
                    bg="white"
                    alignSelf="center"
                  >
                    <QRCodeCanvas // Correct usage
                      value={mfaSetupDetails.provisioning_uri}
                      size={200}
                      level="H"
                      includeMargin={true}
                      bgColor={'#ffffff'}
                      fgColor={'#000000'}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="secondary"
                    textAlign="center"
                  >
                    Or manually enter setup key:{' '}
                    <Typography
                      component="code"
                      variant="caption"
                      fontWeight="medium"
                    >
                      {mfaSetupDetails.secret}
                    </Typography>
                  </Typography>
                  <hr
                    style={{
                      width: '100%',
                      borderTop: '1px solid var(--cipher-color-border-subtle)',
                    }}
                  />
                  <Typography fontWeight="medium">
                    2. Verify and Enable MFA
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    Enter the 6-digit code from your authenticator app below.
                  </Typography>


                  <form onSubmit={handleMfaVerificationAndEnable}>
                    <Input
                      label="Verification Code (TOTP)"
                      id="mfaVerificationCode"
                      value={mfaVerificationCode}
                      onChange={(e) =>
                        setMfaVerificationCode(e.target.value.replace(/\D/g, ''))
                      }
                      disabled={isSubmitting}
                      maxLength={6}
                      placeholder="123456"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        !mfaVerificationCode ||
                        mfaVerificationCode.length !== 6
                      }
                      variant="primary"
                      fullWidth
                    >
                      Verify & Enable MFA
                    </Button>
                  </form>


                </>
              )}
              {/* Close button if process is done (success or error handled and user needs to close) */}
              {((mfaModalError && !isSubmitting) ||
                (mfaModalSuccess && !isSubmitting)) && (
                <Button
                  onClick={() => {
                    setIsMfaModalOpen(false);
                    setMfaUser(null);
                  }}
                  variant="outline"
                  mt="md"
                  alignSelf="flex-end"
                >
                  Close
                </Button>
              )}
            </Stack>
          </Modal>
        )}
      </Stack>
    </Card>
  );
}
export default UserManagementPage;
